import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** UUID that also works on plain HTTP (crypto.randomUUID needs a secure context). */
export function createId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function asArray<T>(value: T | T[] | null | undefined): T[] {
  if (!value) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

export function asObject<T>(value: T | T[] | null | undefined): T | null {
  if (!value) {
    return null;
  }

  return Array.isArray(value) ? (value[0] ?? null) : value;
}

const NAME_TITLES = /^(dr|dr\.|prof|prof\.|phd)$/i;

export function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).slice(0, 2);

  if (parts.length === 0) {
    return "PT";
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("");
}

export function givenName(fullName: string) {
  const parts = fullName.trim().split(/\s+/);
  return parts.find((part) => !NAME_TITLES.test(part)) || parts[0] || "PraxisTárs";
}

export function dayGreeting(date = new Date()) {
  const hour = Number(
    new Intl.DateTimeFormat("hu-HU", {
      hour: "numeric",
      hourCycle: "h23",
      timeZone: "Europe/Budapest",
    })
      .formatToParts(date)
      .find((part) => part.type === "hour")?.value ?? "12",
  );

  if (hour < 5) {
    return "Jó éjszakát";
  }
  if (hour < 10) {
    return "Jó reggelt";
  }
  if (hour < 18) {
    return "Jó napot";
  }
  return "Jó estét";
}

export function formatRelativeTime(isoDate: string) {
  const formatter = new Intl.RelativeTimeFormat("hu", { numeric: "auto" });
  const deltaMs = new Date(isoDate).getTime() - Date.now();
  const deltaMinutes = Math.round(deltaMs / 60_000);
  const deltaHours = Math.round(deltaMs / 3_600_000);
  const deltaDays = Math.round(deltaMs / 86_400_000);

  if (Math.abs(deltaMinutes) < 1) {
    return "épp most";
  }

  if (Math.abs(deltaMinutes) < 60) {
    return formatter.format(deltaMinutes, "minute");
  }

  if (Math.abs(deltaHours) < 24) {
    return formatter.format(deltaHours, "hour");
  }

  if (Math.abs(deltaDays) < 7) {
    return formatter.format(deltaDays, "day");
  }

  return new Intl.DateTimeFormat("hu-HU", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(isoDate));
}

export function formatDate(isoDate: string) {
  return new Intl.DateTimeFormat("hu-HU", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(isoDate));
}

export function formatDateTime(isoDate: string) {
  return new Intl.DateTimeFormat("hu-HU", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(isoDate));
}

export function formatFileSize(bytes: number | null) {
  if (!bytes || bytes <= 0) {
    return null;
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** ASCII-safe extension for Supabase Storage keys (no accents or punctuation). */
export function safeStorageExtension(fileName: string, fallback = "bin") {
  const index = fileName.lastIndexOf(".");
  const raw = index >= 0 ? fileName.slice(index + 1).toLowerCase() : "";
  const safe = raw.replace(/[^a-z0-9]/g, "").slice(0, 12);
  return safe || fallback;
}

export function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return "Váratlan hiba történt.";
}
